import { useState } from 'react';
import { Star, ChevronDown, ChevronRight, FileText } from 'lucide-react';
import StarFilingButton, { useStarredForTicker } from '@/components/StarFilingButton';
import FilingViewer from '@/components/FilingViewer';
import { useAuth } from '@/contexts/AuthContext';

/**
 * StarredFilingsStrip — Round 92o++++++++N++++++++++ (May 2026).
 *
 * Renders the current user's starred filings for a single ticker as
 * a stack of clickable bars below the stock row on the Watchlist
 * page. Clicking a bar expands it inline into a compact FilingViewer
 * showing the SEC document HTML + Copy / Print / PDF / Source / red X.
 *
 * Hidden entirely when the user has no starred filings for the
 * ticker (graceful zero-state).
 */
export default function StarredFilingsStrip({ ticker }) {
  const { user } = useAuth();
  const starred = useStarredForTicker(ticker);
  const [openAcc, setOpenAcc] = useState(null);

  if (!ticker || !Array.isArray(starred) || starred.length === 0) return null;

  return (
    <div
      className="px-3 sm:px-6 pt-1 pb-3 space-y-1"
      data-testid={`starred-filings-strip-${ticker}`}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide font-bold text-amber-700 dark:text-amber-300 mb-1">
        <Star className="w-3 h-3 fill-amber-400 stroke-amber-500" />
        Starred Filings · {ticker}
        <span className="text-muted-foreground font-normal normal-case tracking-normal">
          · {starred.length} pinned
        </span>
      </div>
      {starred.map((row) => {
        const acc = row.accession;
        const isOpen = openAcc === acc;
        const formChip = (row.form_type || 'FILING').toUpperCase();
        const items = Array.isArray(row.item_codes) ? row.item_codes : [];
        const heading = row.title
          || (row.section === 'business' ? 'Business Overview'
            : row.section === 'risk_factors' ? 'Risk Factors'
            : `${formChip} · ${row.filing_date || ''}`);
        return (
          <div
            key={acc}
            className="rounded-md border border-amber-200/60 dark:border-amber-900/50 bg-amber-50/30 dark:bg-amber-950/15 overflow-hidden"
            data-testid={`starred-filing-row-${acc}`}
          >
            <div className="flex items-stretch">
              {/* Own ★ button — Round 92o++++++++N++++++++++ user
                  request: each strip row needs an independent un-star
                  toggle in front of the chevron. Click un-stars JUST
                  this filing (the shared store cascades the change
                  into any open Stock Modal so the matching ★ in the
                  10K/8K/Risks tab flips back to outline). Sits
                  OUTSIDE the toggle button so we don't nest one
                  <button> inside another (invalid HTML). */}
              <div className="flex items-center pl-2 pr-1">
                <StarFilingButton
                  ticker={row.ticker}
                  accession={acc}
                  formType={row.form_type}
                  section={row.section}
                  filingDate={row.filing_date}
                  filingUrl={row.filing_url}
                  itemCodes={row.item_codes}
                  title={row.title}
                  snippet={row.snippet}
                  size="md"
                />
              </div>
              <button
                type="button"
                onClick={() => setOpenAcc(isOpen ? null : acc)}
                className="flex-1 flex items-center gap-2 py-1.5 pr-2.5 text-left hover:bg-amber-100/40 dark:hover:bg-amber-950/30 transition-colors"
                data-testid={`starred-filing-toggle-${acc}`}
              >
                {isOpen
                  ? <ChevronDown className="w-3.5 h-3.5 text-amber-700 dark:text-amber-300 flex-shrink-0" />
                  : <ChevronRight className="w-3.5 h-3.5 text-amber-700 dark:text-amber-300 flex-shrink-0" />}
                <FileText className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                <span className="text-[11px] font-bold uppercase tracking-wide text-amber-700 dark:text-amber-300 flex-shrink-0">
                  {formChip}
                </span>
                {row.filing_date && (
                  <span className="text-[10px] text-muted-foreground flex-shrink-0">
                    {row.filing_date}
                  </span>
                )}
                {items.slice(0, 4).map((c) => (
                  <span
                    key={c}
                    className="px-1 py-0.5 rounded text-[9px] font-bold border border-amber-300/60 dark:border-amber-800/60 bg-amber-100/60 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300 flex-shrink-0"
                  >
                    {c}
                  </span>
                ))}
                <span className="text-[11px] font-medium text-foreground/85 truncate flex-1 min-w-0">
                  {heading}
                </span>
              </button>
            </div>
            {isOpen && (
              <div className="border-t border-amber-200/60 dark:border-amber-900/50 bg-card">
                {row.snippet && (
                  <p className="px-3 py-2 text-[11px] text-foreground/85 leading-relaxed border-b border-border/50">
                    {row.snippet}
                  </p>
                )}
                {row.filing_url ? (
                  <FilingViewer
                    url={row.filing_url}
                    label={`${row.ticker} ${formChip} · ${heading}`}
                    onClose={() => setOpenAcc(null)}
                    user={user}
                    compact
                  />
                ) : (
                  <p className="px-3 py-3 text-[11px] text-muted-foreground italic">
                    No filing URL stored — re-star this filing from the Stock
                    Modal&apos;s 10K / 8K / Risks tab to attach the source.
                  </p>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
